export const USER_PROFILE_NUMERIC_FIELDS = [
  'yearsOfExperience',
  'mortgageIndustryStartYear'
] as const;

type UserProfileNumericField = typeof USER_PROFILE_NUMERIC_FIELDS[number];

type UserProfileNumericInput = Record<UserProfileNumericField, unknown>;

type MarshalledUserProfileNumericFields<T extends UserProfileNumericInput> =
  Omit<T, UserProfileNumericField>
  & Record<UserProfileNumericField, number | null>;

interface NumericFieldRule {
  label: string;
  min: number;
  max?: (currentYear: number) => number;
}

const NUMERIC_FIELD_RULES: Record<UserProfileNumericField, NumericFieldRule> = {
  yearsOfExperience: {
    label: 'Years of Experience',
    min: 0
  },
  mortgageIndustryStartYear: {
    label: 'Mortgage Industry Start Year',
    min: 1950,
    max: (currentYear) => currentYear
  }
};

export class UserProfileNumericFieldError extends Error {
  readonly field: UserProfileNumericField;

  constructor(field: UserProfileNumericField, message: string) {
    super(message);
    this.name = 'UserProfileNumericFieldError';
    this.field = field;
  }
}

const isEmptyNumericValue = (value: unknown) =>
  value === null
  || value === undefined
  || (typeof value === 'string' && value.trim() === '');

export function normalizeUserProfileNumericInput(value: unknown): number | null {
  if (isEmptyNumericValue(value)) {
    return null;
  }

  return typeof value === 'number' ? value : Number(value);
}

function normalizeNumericField(
  field: UserProfileNumericField,
  value: unknown,
  currentYear: number
): number | null {
  const normalized = normalizeUserProfileNumericInput(value);
  const rule = NUMERIC_FIELD_RULES[field];

  if (normalized === null) {
    return null;
  }

  if (!Number.isFinite(normalized) || !Number.isInteger(normalized)) {
    throw new UserProfileNumericFieldError(
      field,
      `${rule.label} must be a whole number or left blank.`
    );
  }

  const maximum = rule.max?.(currentYear);
  if (normalized < rule.min || (maximum !== undefined && normalized > maximum)) {
    const range = maximum === undefined
      ? `${rule.min} or greater`
      : `between ${rule.min} and ${maximum}`;
    throw new UserProfileNumericFieldError(
      field,
      `${rule.label} must be ${range}, or left blank.`
    );
  }

  return normalized;
}

/**
 * React Hook Form can preserve raw strings from number inputs and reset values
 * do not pass through field-level coercers. Normalize the complete update at
 * the final profile-payload boundary so PostgREST receives numbers or null,
 * never an empty string.
 */
export function marshalUserProfileNumericFields<T extends UserProfileNumericInput>(
  data: T,
  currentYear = new Date().getFullYear()
): MarshalledUserProfileNumericFields<T> {
  const payload = { ...data } as Record<string, unknown>;

  for (const field of USER_PROFILE_NUMERIC_FIELDS) {
    payload[field] = normalizeNumericField(field, payload[field], currentYear);
  }

  return payload as MarshalledUserProfileNumericFields<T>;
}
